import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import FormInput from '../../shared/components/ui/FormInput';
import CreateWhatsAppCampaignModal from '../components/forms/CreateWhatsAppCampaignModal';
import Loader from '../../shared/components/ui/Loader';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import { useProjectsList } from '../../../contexts/ProjectsContext';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import { Eye, Layers, Pencil, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  createWhatsAppCampaign,
  getWhatsAppCampaigns,
  getWhatsAppTemplates
} from '../services/api/implementation/impl';

function formatCampaignDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso)
      .toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })
      .replace(/\//g, '.');
  } catch {
    return '—';
  }
}

function normalizeCampaignStatus(raw) {
  const s = String(raw || 'draft').toLowerCase();
  return ['draft', 'active', 'completed'].includes(s) ? s : 'draft';
}

function getCampaignTotalRecipients(row) {
  const metadataSummary = row?.metadata?.data?.data?.summary;
  return (
    row.total_recipients ??
    row.total_leads ??
    0
  );
}

/** Map GET /whatsappCampaign row to card model */
function mapWhatsAppCampaignRow(row, projectList) {
  const project = projectList?.find((p) => String(p.id) === String(row.project_id));
  return {
    id: row.id,
    name: row.name,
    projectType: (row.description && String(row.description).trim()) || project?.name || '—',
    status: normalizeCampaignStatus(row.status),
    created: formatCampaignDate(row.created_at),
    totalLeads: getCampaignTotalRecipients(row)
  };
}

const statusStyles = {
  draft: 'text-red-600 border-red-300/80 bg-rose-50',
  active: 'text-primary border-primary/50 bg-primary/10',
  completed: 'text-gray-600 border-gray-300 bg-gray-100/80'
};

const statusLabel = {
  draft: 'DRAFT',
  active: 'ACTIVE',
  completed: 'COMPLETED'
};

const projectFormSchema = yup.object().shape({
  project: yup.string().required('Select Project')
});

const WhatsAppLeadCampaigns = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { collapsed } = useSidebar();
  const userId = Cookies.get('userId');
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const [campaignsByProject, setCampaignsByProject] = useState(() => ({}));
  const [isCampaignsLoading, setIsCampaignsLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [isResolvingFlow, setIsResolvingFlow] = useState(false);

  const whatsappCampaignsBasePath = useMemo(
    () =>
      location.pathname.includes('/builder/marketing/')
        ? '/builder/marketing/whatsapp-campaigns'
        : '/builder/sales/whatsapp-campaigns',
    [location.pathname]
  );

  const {
    register,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(projectFormSchema),
    defaultValues: { project: '' }
  });

  const projectID = watch('project');
  const projectKey =
    projectID != null && projectID !== '' ? String(projectID) : 'none';

  const campaigns = useMemo(() => {
    if (!projects?.length || projectKey === 'none') return [];
    return campaignsByProject[projectKey] ?? [];
  }, [campaignsByProject, projectKey, projects?.length]);

  const setCampaignsForProject = useCallback(
    (updater) => {
      setCampaignsByProject((prev) => {
        const current = prev[projectKey] ?? [];
        const next = typeof updater === 'function' ? updater(current) : updater;
        return { ...prev, [projectKey]: next };
      });
    },
    [projectKey]
  );

  useEffect(() => {
    if (!projectID || projectKey === 'none' || !projects?.length) {
      return undefined;
    }
    let cancelled = false;
    setIsCampaignsLoading(true);
    getWhatsAppCampaigns(projectID)
      .then((list) => {
        if (cancelled) return;
        const rows = Array.isArray(list) ? list : [];
        const forProject = rows.filter((r) => String(r.project_id) === String(projectID));
        const mapped = forProject.map((row) => mapWhatsAppCampaignRow(row, projects));
        setCampaignsByProject((prev) => ({ ...prev, [projectKey]: mapped }));
      })
      .catch(() => {
        if (!cancelled) {
          toast.error('Could not load WhatsApp campaigns.');
          setCampaignsByProject((prev) => ({ ...prev, [projectKey]: [] }));
        }
      })
      .finally(() => {
        if (!cancelled) setIsCampaignsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectID, projectKey, projects]);

  useEffect(() => {
    mixpanel.track('WhatsApp Lead Campaigns Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (projects?.length > 0 && !projectID) {
      setValue('project', projects[0].id);
    }
  }, [projects, projectID, setValue]);

  const totalCampaigns = campaigns.length;
  const totalLeads = useMemo(
    () => campaigns.reduce((sum, c) => sum + (c.totalLeads || 0), 0),
    [campaigns]
  );

  const handleEditFlow = async (campaign) => {
    mixpanel.track('WhatsApp Lead Campaign Edit Flow', {
      campaign_id: campaign.id,
      user_id: userId
    });
    const id = encodeURIComponent(String(campaign.id));
    const state = { campaignName: campaign.name, campaignStatus: campaign.status };
    setIsResolvingFlow(true);
    try {
      const templates = await getWhatsAppTemplates(campaign.id);
      const hasTemplate = Array.isArray(templates) && templates.length > 0;
      if (hasTemplate) {
        navigate(`${whatsappCampaignsBasePath}/${id}/questionnaire`, { state });
      } else {
        navigate(`${whatsappCampaignsBasePath}/${id}/flow`, { state });
      }
    } catch {
      toast.error('Could not check templates. Opening template editor.');
      navigate(`${whatsappCampaignsBasePath}/${id}/flow`, { state });
    } finally {
      setIsResolvingFlow(false);
    }
  };

  const openCreateModal = useCallback(() => {
    if (!projects?.length) {
      toast.error('Add a project first.');
      return;
    }
    setCreateModalOpen(true);
  }, [projects?.length]);

  const handleCreateModalSubmit = useCallback(
    async ({ campaignName, projectId, description }) => {
      try {
        await createWhatsAppCampaign({
          project_id: Number(projectId),
          name: campaignName,
          description: description || ''
        });
        const list = await getWhatsAppCampaigns(projectId);
        const rows = Array.isArray(list) ? list : [];
        const forProject = rows.filter((r) => String(r.project_id) === String(projectId));
        const mapped = forProject.map((row) => mapWhatsAppCampaignRow(row, projects));
        setCampaignsByProject((prev) => ({ ...prev, [String(projectId)]: mapped }));
        setValue('project', projectId);
        mixpanel.track('WhatsApp Lead Campaign Created', {
          user_id: userId,
          project_id: projectId
        });
        toast.success('Campaign created — configure the flow next.');
        setCreateModalOpen(false);
      } catch (err) {
        const msg = err?.response?.data?.message ?? err?.message;
        toast.error(typeof msg === 'string' ? msg : 'Could not create campaign.');
      }
    },
    [projects, setValue, userId]
  );

  return (
    <>
      <Loader isVisible={isProjectsLoading || isCampaignsLoading || isResolvingFlow} />
      <Layout>
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Header
            backArrow
            onBack={() => navigate('/builder/sales/', { state: { secondaryTab: 'Leads' } })}
            data={{
              name: 'WhatsApp Lead Campaigns',
              subtitle: 'Create and manage lead flow builders for your projects'
            }}
          />
        </div>

        <div className="mt-[4.75rem] min-h-[calc(100vh-6.75rem)] bg-[#F8F9FA] pb-10">
          <div className="px-6 pt-5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex min-w-0 max-w-md flex-1 items-center">
              <FormInput
                width={100}
                label=""
                register={register}
                register_key="project"
                isRequired
                type="dropdown"
                options={[
                  { label: 'Select Project', value: '', disabled: true },
                  ...(projectsForDropdown || [])
                ]}
                errors={errors}
                hideAstrik
              />
            </div>

            <div className="flex flex-wrap gap-3 lg:ml-auto">
              <div className="bg-white rounded-xl shadow-sm border border-[#666666] px-5 py-3 min-w-[9.5rem]">
                <p className="text-[10px] font-semibold tracking-wide text-gray-400 uppercase">
                  Total campaigns
                </p>
                <p className="text-2xl font-semibold tabular-nums mt-0.5 text-primary">
                  {String(totalCampaigns).padStart(2, '0')}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-[#666666] px-5 py-3 min-w-[9.5rem]">
                <p className="text-[10px] font-semibold tracking-wide text-gray-400 uppercase">
                  Total leads
                </p>
                <p className="text-2xl font-semibold tabular-nums mt-0.5 text-primary">
                  {totalLeads}
                </p>
              </div>
            </div>
          </div>

          <div className="px-6 pt-6">
            {!isProjectsLoading && !projects?.length ? (
              <div className="rounded-xl border border-dashed border-gray-300 bg-white/60 py-16 text-center text-gray-500 text-sm">
                No projects available. Add a project in management to manage WhatsApp lead campaigns.
              </div>
            ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
              <button
                type="button"
                onClick={openCreateModal}
                className="h-full min-h-[240px] rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 flex flex-col items-center justify-center gap-3 p-6 text-center transition-colors hover:border-primary/60 hover:bg-primary/10">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center text-white shadow-sm bg-primary">
                  <Plus className="w-7 h-7" strokeWidth={2.5} />
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Create New Campaign</p>
                  <p className="text-xs text-gray-500 mt-1 max-w-[200px] mx-auto">
                    Start building a new WhatsApp lead flow.
                  </p>
                </div>
              </button>

              {campaigns.map((campaign) => (
                <article
                  key={campaign.id}
                  className="group bg-white rounded-[22px] flex flex-col h-full min-h-0 overflow-hidden transition-shadow hover:shadow-lg border-2 border-primary"
                  style={{
                    boxShadow: '0 10px 28px -12px rgba(42, 86, 94, 0.18), 0 4px 12px -6px rgba(42, 86, 94, 0.08)'
                  }}>
                  <div className="p-5 flex gap-3.5 shrink-0">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 text-gray-900 bg-primary/20">
                      <Layers className="w-5 h-5" strokeWidth={2.25} />
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="font-bold text-gray-900 text-base leading-tight truncate">
                            {campaign.name}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1 truncate font-normal">
                            {campaign.projectType}
                          </p>
                        </div>
                        <span
                          className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${statusStyles[campaign.status]}`}>
                          {statusLabel[campaign.status]}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="px-5 pb-4 space-y-2.5 text-sm flex-1 min-h-0">
                    <div className="flex justify-between items-center text-gray-500">
                      <span>Created</span>
                      <span className="text-gray-600 tabular-nums">{campaign.created}</span>
                    </div>
                    <div className="flex justify-between items-center text-gray-500">
                      <span>Total Leads</span>
                      <span className="font-bold tabular-nums text-primary">
                        {campaign.totalLeads}
                      </span>
                    </div>
                  </div>

                  <div className="mt-auto border-t border-gray-200/90 px-4 py-3.5 shrink-0 bg-white">
                    <button
                      type="button"
                      onClick={() => handleEditFlow(campaign)}
                      className="flex w-full items-center justify-center gap-2 py-3 rounded-xl bg-primary text-white text-sm font-semibold shadow-sm transition-colors hover:bg-primary/90 active:opacity-[0.88]">
                      {campaign.status === 'draft' ? (
                        <Pencil className="w-4 h-4 shrink-0" strokeWidth={2} />
                      ) : (
                        <Eye className="w-4 h-4 shrink-0" strokeWidth={2} />
                      )}
                      {campaign.status === 'draft' ? 'Edit Flow' : 'View Flow'}
                    </button>
                  </div>
                </article>
              ))}
            </div>
            )}
          </div>
        </div>

      </Layout>

      <CreateWhatsAppCampaignModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreate={handleCreateModalSubmit}
        projectsForDropdown={projectsForDropdown}
        defaultProjectId={projectID}
      />
    </>
  );
};

export default WhatsAppLeadCampaigns;
